// @flow
import * as React from 'react';
import { isAbortError } from './utilities.js';
import { useValidationApiOrigin } from './validation/ValidationApiOriginContext.js';

export function useValidateWistiaVideoOwnedByMathspace({
  performValidation,
  failedValidation,
  addErrors,
  location,
  videoId,
}: {|
  performValidation: boolean | void,
  failedValidation: boolean,
  addErrors: (errors: Object[]) => void,
  location: {|
    startLineNumber: number,
    startColumn: number,
    endLineNumber: number,
    endColumn: number,
  |},
  videoId: string | void,
|}) {
  const apiOrigin = useValidationApiOrigin();
  React.useEffect(() => {
    if (!performValidation) return;
    if (failedValidation) return;
    // videoId is optional at certain call sites, so will be undefined in
    // those situations. We thus don't want this to trigger an error.
    // At call sites where videoId is mandatory, other spec checks will
    // already be catching the undefined case and prompting the user to
    // provide a videoId prop.
    if (videoId === undefined) return;

    const notOwnedByMathspaceError = {
      message: `${videoId} was not uploaded by Mathspace. Only Mathspace-owned videos are allowed. You are either specifying someone else's video, or the video id doesn't exist on Wistia`,
      ...location,
    };

    const ownerNotDeterminedError = {
      message: `${videoId} needs to be owned by Mathspace but we couldn't determine the owner.`,
      ...location,
    };

    const abortController = new AbortController();

    (async function () {
      try {
        const res = await fetch(
          `${apiOrigin}/api/wistia-video-owner?videoId=${videoId}`,
          { signal: abortController.signal },
        );
        if (res.ok) {
          const { owner, reason, retryAfter } = await res.json();
          if (owner === 'not-mathspace') {
            addErrors([notOwnedByMathspaceError]);
          } else if (owner === 'unknown') {
            if (reason === 'rate-limit-exceeded') {
              addErrors([
                {
                  message: `${videoId} needs to be owned by Mathspace but we couldn't determine the owner due to rate-limiting (>600 req/min). Please wait ${retryAfter} seconds and try again.`,
                  ...location,
                },
              ]);
            } else {
              addErrors([ownerNotDeterminedError]);
            }
          }
        } else {
          addErrors([ownerNotDeterminedError]);
        }
      } catch (error) {
        if (isAbortError(error)) return;
        addErrors([ownerNotDeterminedError]);
      }
    })();

    return () => {
      abortController.abort();
    };
  });
}
